/**
 * Error Boundary Component for BWS Market Maker
 * Shows connectivity errors when services are unavailable
 * NO MOCK DATA - Always shows real errors
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });
    
    console.error('BWS Market Maker Error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    
    // Force a full page reload to retry connections
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isConnectivityError = this.state.error?.message.includes('Failed to fetch') ||
                                 this.state.error?.message.includes('Network Error') ||
                                 this.state.error?.message.includes('Connection failed') ||
                                 this.state.error?.message.includes('ECONNREFUSED');

      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-8">
            <div className="text-center">
              <AlertTriangle className="mx-auto h-16 w-16 text-red-500 mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Service Connection Error
              </h1>
              
              {isConnectivityError ? (
                <div className="mb-6">
                  <p className="text-lg text-gray-700 mb-4">
                    Unable to connect to required services. Please ensure:
                  </p>
                  <div className="text-left bg-gray-50 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Required Services:</h3>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                        <strong>LocalStack:</strong> http://localhost:4566 (AWS services)
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                        <strong>Hardhat:</strong> http://127.0.0.1:8545 (Blockchain RPC)
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                        <strong>DynamoDB Tables:</strong> Seeded with test data
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                        <strong>Smart Contracts:</strong> Deployed to local network
                      </li>
                    </ul>
                  </div>
                  
                  <div className="text-left bg-blue-50 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold text-blue-900 mb-2">Quick Setup Commands:</h3>
                    <div className="font-mono text-sm text-blue-800 space-y-1">
                      <div>1. cd off-chain && docker-compose -f docker-compose.localstack.yml up -d</div>
                      <div>2. cd on-chain && npx hardhat node</div>
                      <div>3. cd on-chain && npx hardhat run scripts/deploy.ts --network localhost</div>
                      <div>4. cd off-chain && node scripts/seed-localstack-data.js</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <p className="text-lg text-gray-700 mb-4">
                    An unexpected error occurred in the BWS Market Maker interface.
                  </p>
                  <div className="text-left bg-gray-50 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Error Details:</h3>
                    <p className="text-sm text-red-600 font-mono">
                      {this.state.error?.message}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={this.handleRetry}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Connection
                </button>
                
                <a
                  href="http://localhost:4566/_localstack/health"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Check LocalStack
                </a>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">No Mock Data Policy</h3>
                <p className="text-sm text-gray-600">
                  This application never uses mock data. All information comes from real 
                  blockchain and database sources. If you see this error, it means the 
                  required services are not running or accessible.
                </p>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                <details className="mt-8 text-left">
                  <summary className="cursor-pointer font-semibold text-gray-700 mb-2">
                    Technical Details (Development)
                  </summary>
                  <pre className="text-xs text-gray-600 bg-gray-100 p-4 rounded overflow-auto">
                    {this.state.error?.stack}
                    {'\n\n'}
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;